import { ApiError } from '@/lib/api/errors';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * KOMUNIKAT NA EKRANIE LOGOWANIA — INNY NIŻ NA EKRANIE DANYCH
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ CO ZGŁOSIŁ WŁAŚCICIEL ────────────────────────────────────────────────────────────────────┐
 * │ Próba logowania kończyła się zdaniem „Sesja wygasła albo nie jesteś zalogowany."            │
 * │ Na FORMULARZU LOGOWANIA jest to zdanie bez sensu: nikt nie miał sesji, która mogłaby       │
 * │ wygasnąć — właśnie próbuje ją założyć. Komunikat kierował uwagę na wygaśnięcie zamiast     │
 * │ powiedzieć, co naprawdę odrzucił serwer.                                                     │
 * │                                                                                              │
 * │ Winowajcą był `describeApiError` — mapper napisany dla EKRANÓW DANYCH, gdzie 401 faktycznie │
 * │ znaczy „twoja sesja się skończyła". Użyty na logowaniu mówi nieprawdę.                       │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ DLACZEGO CYTUJEMY SERWER, ZAMIAST BUDOWAĆ WŁASNĄ TABLICĘ ─────────────────────────────────┐
 * │ Trasa logowania mistrza rozróżnia już SIEDEM sytuacji i dla każdej ma zdanie po polsku:    │
 * │ złe hasło, nieznany login, login niejednoznaczny, brak zsynchronizowanych adresów,         │
 * │ niedostępne źródło tożsamości, brak konta właściciela w konfiguracji, brak danych w ciele. │
 * │                                                                                             │
 * │ Druga tablica w przeglądarce musiałaby te zdania powtórzyć — i rozjechałaby się z serwerem │
 * │ przy pierwszej zmianie. Bierzemy więc `error` z odpowiedzi, a własne zdania trzymamy       │
 * │ WYŁĄCZNIE na sytuacje, w których serwer nic nie powiedział, bo odpowiedź nie doszła.       │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 */

interface CialoBledu {
  error?: unknown;
  powod?: unknown;
}

function tekst(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

/**
 * Zdanie do pokazania pod formularzem logowania.
 *
 * @param err błąd z warstwy API
 * @returns komunikat oraz — gdy serwer go podał — zdanie uzupełniające z powodem
 */
export function komunikatLogowania(err: unknown): { tresc: string; powod: string | null } {
  if (!(err instanceof ApiError)) {
    return { tresc: 'Nieoczekiwany błąd aplikacji.', powod: null };
  }

  const cialo = (err.body ?? {}) as CialoBledu;
  const zSerwera = tekst(cialo.error);
  const powod = tekst(cialo.powod);

  // Serwer powiedział, co odrzucił — to jest najdokładniejsze zdanie, jakie mamy.
  if (zSerwera) return { tresc: zSerwera, powod };

  /*
   * Odpowiedź bez treści albo brak odpowiedzi. Zdania są tu celowo INNE niż na ekranach
   * danych: opisują nieudane LOGOWANIE, a nie utraconą sesję.
   */
  switch (err.kind) {
    case 'network':
      return { tresc: 'Nie udało się połączyć z usługą.', powod: null };
    case 'unauthorized':
      return { tresc: 'Nieprawidłowe dane logowania.', powod: null };
    case 'not-found':
      return { tresc: 'Nie znaleziono restauracji powiązanej z tym loginem.', powod: null };
    case 'rate-limited':
      return {
        tresc: 'Zbyt wiele prób logowania. Odczekaj chwilę i spróbuj ponownie.',
        powod: null,
      };
    case 'server':
      return { tresc: 'Usługa zwróciła błąd. Spróbuj ponownie za chwilę.', powod: null };
    default:
      return { tresc: 'Nie udało się zalogować.', powod: null };
  }
}
